import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { MessageSquare, Trash2, Reply, Send, Film, ChevronDown, ChevronUp } from 'lucide-react';

const CommentManager = () => {
  const { api } = useAuth();
  const [videos, setVideos] = useState([]);
  const [comments, setComments] = useState([]);
  const [total, setTotal] = useState(0);
  const [expandedVideo, setExpandedVideo] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replies, setReplies] = useState({});

  const load = async () => {
    try {
      const [vRes, cRes] = await Promise.all([
        api.get('/admin/videos?limit=100'),
        api.get('/admin/comments?limit=200')
      ]);
      setVideos(vRes.data.videos || []);
      setComments(cRes.data.comments || []);
      setTotal(cRes.data.total || 0);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { load(); }, []);

  const loadReplies = async (commentId) => {
    try {
      const r = await api.get(`/comments/${commentId}/replies`);
      if (r.data.length > 0) {
        setReplies(prev => ({ ...prev, [commentId]: r.data }));
      }
    } catch (e) {}
  };

  const toggleVideo = async (videoId) => {
    if (expandedVideo === videoId) {
      setExpandedVideo(null);
    } else {
      setExpandedVideo(videoId);
      const videoComments = comments.filter(c => c.video_id === videoId);
      for (const c of videoComments) {
        await loadReplies(c.id);
      }
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this comment?')) return;
    await api.delete(`/admin/comments/${id}`); load();
  };

  const handleReply = async (commentId) => {
    if (!replyText.trim()) return;
    try {
      await api.post(`/admin/comments/${commentId}/reply`, { text: replyText.trim() });
      setReplyText(''); setReplyTo(null);
      await loadReplies(commentId);
    } catch (err) { alert('Error sending reply'); }
  };

  // Group comments by video
  const getVideoComments = (videoId) => comments.filter(c => c.video_id === videoId);

  // Videos that have comments
  const videosWithComments = videos.filter(v => getVideoComments(v.id).length > 0);
  // Orphan comments (video deleted)
  const orphanComments = comments.filter(c => !videos.find(v => v.id === c.video_id));

  return (
    <div className="admin-section">
      <h2 className="section-title"><MessageSquare size={20} /> Comments ({total})</h2>

      {videosWithComments.length === 0 && orphanComments.length === 0 ? (
        <div className="admin-card">
          <p className="empty-text">No comments yet</p>
        </div>
      ) : (
        <>
          {videosWithComments.map(v => {
            const vComments = getVideoComments(v.id);
            const isOpen = expandedVideo === v.id;

            return (
              <div key={v.id} className="admin-card" style={{ padding: 0, marginBottom: 10, overflow: 'hidden' }}>
                <button
                  onClick={() => toggleVideo(v.id)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                    padding: '14px 16px', background: 'transparent', border: 'none',
                    cursor: 'pointer', textAlign: 'left', transition: 'background 0.15s'
                  }}
                  onMouseEnter={e => e.target.style.background = 'rgba(255,255,255,0.02)'}
                  onMouseLeave={e => e.target.style.background = 'transparent'}
                >
                  <img src={v.thumbnail || '/logo.png'} alt="" style={{ width: 60, height: 34, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#ddd', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.title}</div>
                    <div style={{ fontSize: 11, color: '#555' }}>{v.category || ''}</div>
                  </div>
                  <span className="badge badge-warning" style={{ flexShrink: 0 }}>{vComments.length} comments</span>
                  {isOpen ? <ChevronUp size={16} style={{ color: '#888', flexShrink: 0 }} /> : <ChevronDown size={16} style={{ color: '#888', flexShrink: 0 }} />}
                </button>

                {isOpen && (
                  <div style={{ borderTop: '1px solid rgba(255,255,255,0.04)', padding: '12px 16px' }}>
                    {vComments.map(c => (
                      <div key={c.id} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                          <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg, #f59e0b, #ef4444)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#fff', fontWeight: 800, flexShrink: 0 }}>{(c.user || 'A')[0]}</div>
                          <span style={{ fontSize: 13, fontWeight: 700, color: '#f59e0b' }}>{c.user}</span>
                          <span style={{ fontSize: 10, color: '#555', marginLeft: 'auto' }}>{new Date(c.created_at).toLocaleString()}</span>
                        </div>
                        <p style={{ fontSize: 13, color: '#bbb', padding: '8px 12px', background: 'rgba(255,255,255,0.03)', borderRadius: 8, borderLeft: '3px solid rgba(255,255,255,0.06)', marginBottom: 6 }}>{c.text}</p>

                        {replies[c.id] && replies[c.id].map(r => (
                          <div key={r.id} style={{ marginLeft: 24, padding: '8px 12px', background: 'rgba(245,158,11,0.05)', borderRadius: 8, borderLeft: '3px solid #f59e0b', marginBottom: 4 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                              <span style={{ fontSize: 11, fontWeight: 800, color: '#f59e0b' }}>Admin</span>
                              <span style={{ fontSize: 10, color: '#555' }}>{new Date(r.created_at).toLocaleString()}</span>
                            </div>
                            <p style={{ fontSize: 12, color: '#bbb' }}>{r.text}</p>
                          </div>
                        ))}

                        {replyTo === c.id ? (
                          <div style={{ display: 'flex', gap: 6, marginTop: 6, marginLeft: 24 }}>
                            <input className="form-input" style={{ flex: 1, margin: 0, fontSize: 12 }} placeholder="Reply as Admin..."
                              value={replyText} onChange={e => setReplyText(e.target.value)}
                              onKeyDown={e => e.key === 'Enter' && handleReply(c.id)} autoFocus />
                            <button className="btn-primary" style={{ margin: 0, padding: '6px 12px', fontSize: 11 }} onClick={() => handleReply(c.id)}><Send size={12} /></button>
                            <button className="btn-secondary" style={{ margin: 0, padding: '6px 12px', fontSize: 11 }} onClick={() => { setReplyTo(null); setReplyText(''); }}>Cancel</button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                            <button className="btn-sm btn-edit" onClick={() => setReplyTo(c.id)}><Reply size={11} /> Reply</button>
                            <button className="btn-sm btn-delete" onClick={() => handleDelete(c.id)}><Trash2 size={11} /> Delete</button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {orphanComments.length > 0 && (
            <div className="admin-card" style={{ marginTop: 16 }}>
              <h3 className="card-subtitle">Other Comments ({orphanComments.length})</h3>
              {orphanComments.map(c => (
                <div key={c.id} style={{ padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                  <span style={{ color: '#f59e0b', fontWeight: 700, fontSize: 12 }}>{c.user}</span>
                  <span style={{ color: '#555', fontSize: 10, marginLeft: 8 }}>{new Date(c.created_at).toLocaleString()}</span>
                  <p style={{ color: '#bbb', fontSize: 12, marginTop: 4 }}>{c.text}</p>
                  <button className="btn-sm btn-delete" style={{ marginTop: 4 }} onClick={() => handleDelete(c.id)}><Trash2 size={11} /> Delete</button>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CommentManager;
